import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Route-level SEO head manager.
 *
 * Sets <title>, meta description, canonical, Open Graph, Twitter and — when
 * `jsonLd` is provided — a JSON-LD structured-data block. Kept intentionally
 * small so pages can drop it in without ceremony.
 *
 * Note: index.html already carries org-level JSON-LD (Organization,
 * LocalBusiness, WebSite). Pages should add *page-specific* schema here
 * (Service, WebPage, BreadcrumbList).
 */
const SITE_ORIGIN = "https://www.powerbuilt.sx";

export default function SEO({
  title,
  description,
  path = "/",
  image = "/og-cover.jpg",
  noindex = false,
  jsonLd = null,
}) {
  const url = `${SITE_ORIGIN}${path}`;
  const imgAbs = image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`;
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="PowerBuilt" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imgAbs} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imgAbs} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

export { SITE_ORIGIN };
